import { createContext, useContext } from 'react';

/**
 * Imperative control surface for the notifications popover.
 *
 * Provided by `NotificationsPopoverContent` and consumed by descendants
 * (e.g. notification card action click) so they can:
 *
 * - `skipFocusReturn()` — one-shot flag to skip Radix's default focus
 *   restore on the next close. Without it, the bell trigger re-focuses
 *   immediately after the popover closes and its tooltip pops on top of
 *   the just-navigated-to page.
 * - `close()` — imperatively close the popover after an action (e.g.
 *   navigating from a notification card).
 *
 * Scoping to a context (rather than a module-level flag) means the
 * mobile-sheet surface — which has no provider — gets a no-op.
 *
 * Lives in its own module so the card can consume it without dragging
 * the popover content's transitive imports (Radix popper, dropdown
 * menu, posthog flag reads) into unit tests.
 */
export type NotificationsPopoverControl = {
  skipFocusReturn: () => void;
  close: () => void;
};

export const NotificationsPopoverControlContext =
  createContext<NotificationsPopoverControl | null>(null);

const noop = () => {};
const noopControl: NotificationsPopoverControl = {
  skipFocusReturn: noop,
  close: noop,
};

/**
 * Returns the popover control surface. No-op values when the caller is
 * not rendered inside `<NotificationsPopoverContent>`.
 */
export const useNotificationsPopoverControl = (): NotificationsPopoverControl => {
  return useContext(NotificationsPopoverControlContext) ?? noopControl;
};
